import { useEffect, useRef } from 'react';
import { usedPercentOf, formatUsageFigure } from '@brunel/shared';
import type {
  AiUsagePolicyRow,
  ProviderUsageLatest,
  FinishedRun,
  MissionTask,
  Project,
  RunPoolUsageRow,
  RunningTask,
  UsageMeasurementRow,
} from '../lib/data';
import {
  isExternalAiStatusMeter,
  isLocalRuntimeMeter,
  policyFromRow,
  type AiCapacityMeter,
  type ExternalAiStatusMeter,
  type LocalRuntimeCapacityMeter,
} from '../lib/ai-usage-view';
import { AiUsageGauge } from './AiUsageGauge';

export interface AiUsageDetailProps {
  readonly meters: readonly AiCapacityMeter[];
  readonly selectedKey?: string | null;
  readonly onSelect?: (key: string) => void;
  readonly focusRequest?: number;
  readonly policy?: AiUsagePolicyRow | null;
  readonly readings?: readonly ProviderUsageLatest[];
  readonly measurements: readonly UsageMeasurementRow[];
  readonly runUsage: readonly RunPoolUsageRow[];
  readonly projects: readonly Project[];
  readonly tasks: readonly MissionTask[];
  readonly running: readonly RunningTask[];
  readonly results: readonly FinishedRun[];
}

function matchesMeter(
  meter: AiCapacityMeter,
  row: Pick<UsageMeasurementRow, 'provider_key' | 'pool_key' | 'runtime_key' | 'model_key'>,
): boolean {
  if (row.provider_key !== meter.provider) return false;
  if (meter.poolKey !== null && row.pool_key !== meter.poolKey) return false;
  if (meter.runtime !== null && row.runtime_key !== meter.runtime) return false;
  if (meter.model !== null && row.model_key !== meter.model) return false;
  return true;
}

function formatWhen(value: string): string {
  const at = Date.parse(value);
  if (Number.isNaN(at)) return value;
  return new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function figure(value: number | null, unit: string): string {
  return formatUsageFigure(value, unit) ?? 'Unknown';
}

function taskContext(
  assignmentId: string,
  projects: readonly Project[],
  tasks: readonly MissionTask[],
  running: readonly RunningTask[],
  results: readonly FinishedRun[],
): { task: string; worker: string; project: string } {
  const live = running.find((row) => row.assignment_id === assignmentId) ?? null;
  if (live !== null) {
    return {
      task: live.title,
      worker: live.worker_name ?? (live.worker_number === null ? 'Worker unknown' : `Worker ${live.worker_number}`),
      project: live.project_name,
    };
  }
  const done = results.find((row) => row.assignment_id === assignmentId) ?? null;
  if (done !== null) {
    return {
      task: tasks.find((task) => task.id === done.task_id)?.title ?? `Task ${done.task_id.slice(0, 8)}`,
      worker: done.worker_number === null ? 'Worker unknown' : `Worker ${done.worker_number}`,
      project: projects.find((project) => project.id === done.project_id)?.name ?? `Project ${done.project_id.slice(0, 8)}`,
    };
  }
  return { task: `Assignment ${assignmentId.slice(0, 8)}`, worker: 'Worker unknown', project: 'Project unknown' };
}

function percentFact(value: number | null): string {
  return value === null || !Number.isFinite(value) ? 'Unknown' : `${Math.round(value * 10) / 10}%`;
}

function whenFact(value: string | null): string {
  return value === null ? 'Unknown' : formatWhen(value);
}


function ExternalAiStatusDetailCard({ meter }: { readonly meter: ExternalAiStatusMeter }) {
  const status = meter.externalAi;
  const capability = status.entityKind === 'executable_runtime'
    ? status.connectionState === 'available' ? 'Executable ready' : status.connectionState === 'unavailable' ? 'Runtime unavailable' : 'Runtime Unknown'
    : status.effectiveCapability.replaceAll('_', ' ');
  const capacity = meter.usedPercent === null
    ? 'Unknown'
    : String(Math.round(meter.usedPercent * 10) / 10) + '% used';
  return (
    <article className={'ai-usage-detail-card tone-' + meter.tone} data-meter-kind="external-ai-status-detail">
      <AiUsageGauge meter={meter} size="detail" />
      <dl className="ai-usage-detail-facts">
        <div><dt>Entity</dt><dd>{status.entityKey}</dd></div>
        <div><dt>Kind</dt><dd>{status.entityKind.replaceAll('_', ' ')}</dd></div>
        <div><dt>Status</dt><dd>{status.connectionState.replaceAll('_', ' ')}</dd></div>
        <div><dt>Capability</dt><dd>{capability}</dd></div>
        <div><dt>Capacity</dt><dd>{capacity}</dd></div>
        <div><dt>Remaining</dt><dd>{meter.remainingLabel ?? 'Unknown'}</dd></div>
        <div><dt>Model</dt><dd>{meter.model ?? 'Unknown'}</dd></div>
        <div><dt>Runtime</dt><dd>{meter.runtime ?? 'Not applicable'}</dd></div>
        <div><dt>Installed</dt><dd>{status.installed === null ? 'Unknown' : status.installed ? 'Yes' : 'No'}</dd></div>
        <div><dt>Auth</dt><dd>{status.authState.replaceAll('_', ' ')}</dd></div>
        <div><dt>Pool</dt><dd>{meter.poolKey ?? 'Unknown / not applicable'}</dd></div>
        <div><dt>Last verified</dt><dd>{whenFact(status.lastVerifiedAt)}</dd></div>
        <div><dt>Evidence source</dt><dd>{status.evidenceSource}</dd></div>
        <div><dt>Billing</dt><dd>{meter.billingKind.replaceAll('_', ' ')}</dd></div>
      </dl>
      <p className="muted">{status.note}</p>
    </article>
  );
}

function LocalRuntimeDetailCard({ meter }: { readonly meter: LocalRuntimeCapacityMeter }) {
  const local = meter.localRuntime;
  const state = local.state.replaceAll('_', ' ');
  return (
    <article className={`ai-usage-detail-card tone-${meter.tone}`} data-meter-kind="local-runtime-detail">
      <AiUsageGauge meter={meter} size="detail" />
      <dl className="ai-usage-detail-facts">
        <div><dt>Computer</dt><dd>{local.nodeName}</dd></div>
        <div><dt>Node state</dt><dd>{local.nodeStatus || 'Unknown'}</dd></div>
        <div><dt>Installed</dt><dd>{local.installed === null ? 'Unknown' : local.installed ? 'Yes' : 'No'}</dd></div>
        <div><dt>Service health</dt><dd>{local.serviceState}</dd></div>
        <div><dt>Runtime available</dt><dd>{local.runtimeAvailable === null ? 'Unknown' : local.runtimeAvailable ? 'Available' : 'Unavailable'}</dd></div>
        <div><dt>Runtime version</dt><dd>{local.runtimeVersion ?? 'Unknown'}</dd></div>
        <div><dt>Resource state</dt><dd>{state}</dd></div>
        <div><dt>Machine memory free</dt><dd>{percentFact(local.memoryFreePercent)}</dd></div>
        <div><dt>Video memory free</dt><dd>{percentFact(local.videoMemoryFreePercent)}</dd></div>
        <div><dt>CPU load</dt><dd>{percentFact(local.cpuLoadPercent)}</dd></div>
        <div><dt>Graphics utilization</dt><dd>{percentFact(local.graphicsUtilPercent)}</dd></div>
        <div><dt>Capacity accepting work</dt><dd>{local.accepting === null ? 'Unknown' : local.accepting ? 'Yes' : 'No'}</dd></div>
        <div><dt>Capacity note</dt><dd>{local.holdingBack ?? 'None recorded'}</dd></div>
        <div><dt>Last probe</dt><dd>{whenFact(local.lastProbeAt)}</dd></div>
        <div><dt>Last successful local probe</dt><dd>{whenFact(local.lastSuccessfulProbeAt)}</dd></div>
        <div><dt>Account quota</dt><dd>Not applicable · Local / Unlimited</dd></div>
        <div><dt>Privacy</dt><dd>Local runtime evidence stays node-scoped; no account sign-in is required.</dd></div>
      </dl>

      <section className="ai-usage-history" aria-label={`${meter.title} installed models on ${local.nodeName}`}>
        <h4>Installed models</h4>
        {!local.installedModelsKnown ? (
          <span className="ai-usage-trend-empty">Unknown — local model inventory has not been confirmed.</span>
        ) : local.installedModels.length === 0 ? (
          <span className="ai-usage-trend-empty">No installed models reported.</span>
        ) : (
          <ul className="ai-usage-history-list">
            {local.installedModels.map((model) => (
              <li key={model.name}>
                <strong>{model.name}</strong>
                <span>{model.fileSize ?? 'File size unknown'}</span>
              </li>
            ))}
          </ul>
        )}
        <small className="muted">
          Routing offerings on this computer: {local.offeringModels.length > 0 ? local.offeringModels.join(' · ') : 'Unknown'}
        </small>
      </section>

      <section className="ai-usage-breakdown" aria-label={`${meter.title} loaded models on ${local.nodeName}`}>
        <h4>Loaded / running models</h4>
        {!local.loadedModelsKnown ? (
          <span className="ai-usage-trend-empty">Unknown — loaded-model state was not reported.</span>
        ) : local.loadedModels.length === 0 ? (
          <span className="ai-usage-trend-empty">No loaded model reported.</span>
        ) : (
          <ul className="ai-usage-breakdown-list">
            {local.loadedModels.map((model) => (
              <li key={model.name}>
                <span>
                  <strong>{model.name}</strong>
                  <small className="muted">
                    {[
                      model.processor ? `processor ${model.processor}` : null,
                      model.contextLength ? `context ${model.contextLength}` : null,
                    ].filter(Boolean).join(' · ') || 'Processor/context unknown'}
                  </small>
                </span>
                <span>{model.loadedSize ?? 'Loaded size unknown'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}

export function AiUsageDetail({
  meters,
  selectedKey = null,
  onSelect,
  focusRequest = 0,
  policy,
  readings = [],
  measurements,
  runUsage,
  projects,
  tasks,
  running,
  results,
}: AiUsageDetailProps) {
  const detailRef = useRef<HTMLElement>(null);
  const activeKey = selectedKey ?? meters[0]?.key ?? null;
  const visibleMeters = onSelect ? meters.filter((meter) => meter.key === activeKey) : meters;
  const limits = policyFromRow(policy);
  useEffect(() => {
    if (focusRequest > 0) {
      detailRef.current?.focus({ preventScroll: true });
      detailRef.current?.closest('.inspector-body')?.scrollTo({ top: 0 });
    }
  }, [activeKey, focusRequest]);
  if (meters.length === 0) {
    return (
      <div className="inspector-empty" role="status">
        <p>No AI usage readings yet</p>
        <p className="muted">Brunel will keep percentages Unknown until a provider or tool states enough allowance data to calculate one.</p>
      </div>
    );
  }

  return (
    <section className="ai-usage-detail" aria-label="AI usage detail" ref={detailRef} tabIndex={-1}>
      <header className="stack tight">
        <h3>AI Usage</h3>
        <small className="muted">Provider readings and run deltas already recorded by Brunel. Unknown values stay unknown.</small>
      </header>

      {onSelect && <label className="ai-usage-detail-picker">Provider / model
        <select value={activeKey ?? ''} onChange={(event) => onSelect(event.target.value)}>
          {visibleMeters.length === 0 && <option value={activeKey ?? ''}>Unknown — reading unavailable</option>}
          {meters.map((meter) => <option key={meter.key} value={meter.key}>
            {isLocalRuntimeMeter(meter)
              ? `${meter.title} · ${meter.localRuntime.nodeName}`
              : isExternalAiStatusMeter(meter)
                ? meter.title
                : `${meter.model ?? meter.title} · ${meter.provider}${meter.runtime ? ` · ${meter.runtime}` : ''}`}
          </option>)}
        </select>
      </label>}
      {visibleMeters.length === 0 && <p role="status">Unknown — selected reading is unavailable.</p>}
      <div className="ai-usage-detail-list">
        {visibleMeters.map((meter) => {
          if (isLocalRuntimeMeter(meter)) return <LocalRuntimeDetailCard key={meter.key} meter={meter} />;
          if (isExternalAiStatusMeter(meter)) return <ExternalAiStatusDetailCard key={meter.key} meter={meter} />;
          const latest = readings.find((row) => [row.provider_key, row.account_ref ?? '', row.pool_key ?? '', row.runtime_key ?? ''].join(':') === meter.key);
          const history = measurements
            .filter((row) => matchesMeter(meter, row) && (!latest || row.account_ref === latest.account_ref))
            .sort((a, b) => Date.parse(b.observed_at) - Date.parse(a.observed_at))
            .slice(0, 10)
            .reverse();
          const points = history
            .map((row) => ({
              at: row.observed_at,
              percent: usedPercentOf(row.used_value, row.remaining_value, row.limit_value),
              label: [
                figure(row.used_value, row.measure_unit) + ' used',
                row.remaining_value === null ? null : figure(row.remaining_value, row.measure_unit) + ' remaining',
              ].filter(Boolean).join(' · '),
            }));
          const knownPoints = points.filter((point): point is typeof point & { percent: number } => point.percent !== null);
          const latestReset = latest ? latest.resets_at : history.at(-1)?.resets_at ?? null;
          const breakdown = runUsage
            .filter((row) => row.provider_key === meter.provider && (meter.poolKey === null || row.pool_key === meter.poolKey) && (!latest || row.account_ref === latest.account_ref))
            .slice(0, 6);

          return (
            <article key={meter.key} className={`ai-usage-detail-card tone-${meter.tone}`}>
              <AiUsageGauge meter={meter} size="detail" />

              <dl className="ai-usage-detail-facts">
                <div><dt>Provider</dt><dd>{meter.provider || 'Unknown'}</dd></div>
                <div><dt>Used</dt><dd>{meter.usedLabel ?? 'Unknown'}</dd></div>
                <div><dt>Remaining</dt><dd>{meter.remainingLabel ?? 'Unknown'}</dd></div>
                <div><dt>Model</dt><dd>{meter.model ?? 'Unknown'}</dd></div>
                <div><dt>Runtime</dt><dd>{meter.runtime ?? 'Unknown'}</dd></div>
                <div><dt>Reset time</dt><dd>{latestReset === null ? (meter.resetsLabel ?? 'Unknown') : formatWhen(latestReset)}</dd></div>
                <div><dt>Source</dt><dd>{meter.measurementLabel ?? 'Unknown'} · {meter.measurementPhrase}</dd></div>
                <div><dt>Quota</dt><dd>{meter.quotaSignal.replaceAll('_', ' ')}</dd></div>
                <div><dt>Hosting</dt><dd>{meter.hosting}</dd></div>
                <div><dt>Billing</dt><dd>{meter.billingKind.replaceAll('_', ' ')}</dd></div>
                <div><dt>App</dt><dd>Brunel</dd></div>
                <div><dt>Pool</dt><dd>{meter.poolKey ?? 'Unknown'}</dd></div>
                <div><dt>Outage / exhaustion</dt><dd>{meter.quotaSignal === 'exhausted' ? 'Exhausted' : meter.quotaSignal === 'signed_out' ? 'Unavailable — signed out' : meter.quotaSignal === 'rate_limited' ? 'Rate limited' : 'Unknown'}</dd></div>
                <div><dt>Warning thresholds</dt><dd>{limits.warnThresholds.map((value) => `${value}%`).join(' · ')}</dd></div>
                <div><dt>Soft / hard limits</dt><dd>{limits.softLimitPercent}% / {limits.hardLimitPercent}%</dd></div>
                <div><dt>Reserve policy</dt><dd>{limits.reservePercent}% · {limits.exhaustionBehavior.replaceAll('_', ' ')}</dd></div>
                <div><dt>Policy source</dt><dd>{policy ? 'Saved policy' : 'Default policy'}</dd></div>
              </dl>

              <section className="ai-usage-history" aria-label={`${meter.title} usage history`}>
                <h4>History / trend</h4>
                {knownPoints.length >= 2 ? (
                  <div className="ai-usage-trend" aria-label="Recorded usage percentage trend">
                    {knownPoints.map((point, index) => (
                      <span
                        key={`${point.at}:${index}`}
                        className="ai-usage-trend-bar"
                        style={{ height: `${Math.max(4, Math.min(100, point.percent))}%` }}
                        title={`${formatWhen(point.at)} · ${Math.round(point.percent * 10) / 10}% used`}
                      />
                    ))}
                  </div>
                ) : (
                  <span className="ai-usage-trend-empty">Unknown — not enough recorded percentage points for a trend.</span>
                )}
                <ul className="ai-usage-history-list">
                  {points.slice(-4).reverse().map((point) => (
                    <li key={point.at}>
                      <span>{formatWhen(point.at)}</span>
                      <span>{point.percent === null ? point.label || 'Unknown' : `${Math.round(point.percent * 10) / 10}% used`}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="ai-usage-breakdown" aria-label={`${meter.title} task breakdown`}>
                <h4>Worker / project / task breakdown</h4>
                <small className="muted">Recorded pool usage; not a per-model allocation.</small>
                {breakdown.length === 0 ? (
                  <span className="ai-usage-trend-empty">Unknown — no comparable per-run usage deltas are recorded for this pool yet.</span>
                ) : (
                  <ul className="ai-usage-breakdown-list">
                    {breakdown.map((row) => {
                      const context = taskContext(row.assignment_id, projects, tasks, running, results);
                      const delta = row.used_delta !== null
                        ? `${figure(row.used_delta, row.measure_unit)} used`
                        : row.remaining_delta !== null
                          ? `${figure(row.remaining_delta, row.measure_unit)} consumed`
                          : 'Delta unknown';
                      return (
                        <li key={`${row.assignment_id}:${row.measured_after}`}>
                          <span>
                            <strong>{context.task}</strong>
                            <small className="muted">{context.worker} · {context.project}</small>
                          </span>
                          <span>{delta}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </article>
          );
        })}
      </div>
    </section>
  );
}
